import type { RoleResponse, SettingRow } from '@palaccount/contracts';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ADM-01 "Viewing as" — a DEMONSTRATION, not a permission switch (OQ-15-11)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Delegated ruling D38 (27 Aug 2026) built this: OQ-15-11's answer is
 * "Yes, but only for the Owner and the Administrator role, and clearly
 * labelled as a preview. Add **no role** as a fourth thing to preview."
 *
 * ── PREVIEW, NOT IMPERSONATION, AND THE SPEC SAYS SO IN ITS OWN WORDS ──────
 *
 * ADM-01 §6, the control's own row (spec line 33446), verbatim:
 *
 *   "Redraws the whole screen from another role's point of view, so the Owner
 *    can see what an ordinary person sees. **This is a demonstration control,
 *    not a real permission switch.** Its three choices should become four
 *    under owner ruling R18 — Owner, Admin, a chosen role, and **no role** —
 *    because the last of those is now a state a real person can be in and the
 *    hardest one to picture."
 *
 * "Not a real permission switch" is the whole security design, written by the
 * document. So NOTHING in this file, and nothing that calls it, touches a
 * request. There is no header, no query parameter and no body field naming a
 * previewed role anywhere in this build; every request the screen makes while
 * a preview is running is byte-for-byte the request it makes with the preview
 * off, carrying the caller's own session and answered by the caller's own
 * permissions. `adm01-viewing-as.test.tsx` pins that by capturing both
 * requests and comparing them, which is a test that goes red the day somebody
 * turns this into impersonation.
 *
 * BR-PERM-50 already forbids the other direction — "hiding a control must
 * never be the only thing that prevents an action" — and this file is the
 * mirror image of that sentence: SHOWING a control here must never be the
 * thing that permits one either. It draws; the server decides.
 *
 * ── WHY THIS IS A PROJECTION OF THE ROWS RATHER THAN A SECOND RENDERER ─────
 *
 * The screen's job is to show "what an ordinary person sees", so the preview
 * has to reproduce what THIS BUILD draws for that person, not what a reading
 * of §15.6 says it ought to. Those are different claims and only the first is
 * useful: a preview modelled on the spec would quietly disagree with the
 * screen the previewed person actually opens.
 *
 * So the preview does exactly what the SERVER does when it answers
 * `GET /api/settings` for that person — drop the rows they may not see, and
 * recompute `canChange` — and then hands the result to the same rendering
 * code, untouched. Everything downstream falls out for free: the rail's group
 * counts (BR-ADM-01-06), the dimming (BR-ADM-01-07), the padlock, the search.
 * `SettingsService.list()` in `apps/api/src/settings/settings.service.ts` is
 * the function being mirrored, and `previewRows` below is written against it
 * line for line.
 *
 * ── WHAT AUTHORITY EACH CHOICE CARRIES, AND WHERE IT COMES FROM ────────────
 *
 * ADM-01 §5's "Who may set which scope" table (spec lines 33393–33397):
 *
 *   The Owner (the FLAG, not a role)  Owner, Organization, Role — and their own
 *   Administrator (shipped role)      Organization, Role — and their own.
 *                                     "Never the six owner-only ones, which
 *                                      are invisible to them."
 *   Anybody else, with a role or      Only their own personal ones — plus any
 *   without one                       unlocked Organization or Role setting
 *
 * Owner, Administrator and "no role" are therefore CONSTANTS: their authority
 * over this screen is fully written down and needs no lookup. Only "a chosen
 * role" needs the roles grid, which the Owner and the Administrator can
 * already read (`GET /api/roles`, `admin.view`, BR-PERM-05) — so the preview
 * adds no server surface at all. A build that could not read the grid would
 * still offer the other three.
 *
 * ── AND THE CHOICE THE SPEC CALLS THE HARDEST ONE TO PICTURE ───────────────
 *
 * A role that holds no `admin.view`, and a person holding no role, CANNOT OPEN
 * ADM-01. ADM-01 §2 says both in as many words — "AP Clerk … cannot open this
 * screen at all", "A user with no role … cannot open this screen either" — so
 * the honest preview of those is not a shorter rail, it is the screen being
 * absent (BR-PERM-19/BR-PERM-43: absent, never greyed). `canOpen` below is
 * that, and the caller draws the closed door rather than a stripped screen.
 */

/** The four things OQ-15-11 asks to be previewable, plus the off state. */
export type ViewAsTarget =
  | { readonly kind: 'self' }
  | { readonly kind: 'owner' }
  | { readonly kind: 'admin' }
  | { readonly kind: 'role'; readonly roleId: string }
  | { readonly kind: 'noRole' };

export const VIEW_AS_OFF: ViewAsTarget = { kind: 'self' };

/**
 * What a previewed identity may do ON THIS SCREEN. Deliberately three
 * booleans and not a permission list: the settings ladder is "a second,
 * separate ladder" from the permission grid (PART 16.6), and pretending
 * otherwise is how a preview starts disagreeing with the server.
 */
export interface PreviewAuthority {
  /** The Owner FLAG. Decides whether the six owner-only rows exist at all. */
  readonly isOwner: boolean;
  /** BR-ADM-01-04's "the Owner or a holder of the Administrator role". */
  readonly canEditCompany: boolean;
  /** ADM-01 §2 — whether this identity can reach the screen in the first place. */
  readonly canOpen: boolean;
}

/**
 * The build's spelling of "the Administrator role" on this screen.
 *
 * `SettingsService` gates every company-value write with
 * `isOwner || holds('admin.edit')` and cites BR-ADM-01-04 for it, so
 * `admin.edit` IS how this build asks "is this the Administrator" here. The
 * preview asks the same question of a chosen role rather than inventing a
 * second one — two predicates for one rule is how a preview and a server drift
 * apart while both look right.
 */
const COMPANY_SETTINGS_PERMISSION = 'admin.edit';

/** ADM-01 §3 — "The whole General Settings menu group is absent for a role
 *  holding nothing on `admin`"; the route itself sits in the `admin` area. */
const OPEN_SCREEN_PERMISSION = 'admin.view';

/**
 * The authority a preview target carries.
 *
 * `roles` is only consulted for the `role` kind. A chosen role that is not in
 * the list — the grid failed to load, or the role was deactivated in another
 * tab — resolves to the LEAST authority rather than to the caller's own. That
 * direction is deliberate: a preview that silently falls back to "you" would
 * show the Owner their own screen while a banner claimed otherwise, which is
 * the one failure this control cannot afford.
 */
export function previewAuthority(
  target: ViewAsTarget,
  roles: readonly RoleResponse[] = [],
): PreviewAuthority {
  switch (target.kind) {
    case 'owner':
      return { isOwner: true, canEditCompany: true, canOpen: true };
    case 'admin':
      return { isOwner: false, canEditCompany: true, canOpen: true };
    case 'noRole':
      // BR-PERM-73 / ADM-01 §2 — role-less is a real state, and this screen is
      // not one of the places it reaches. Their personal settings live on
      // ADM-08, which carries no permission at all.
      return { isOwner: false, canEditCompany: false, canOpen: false };
    case 'role': {
      const grid = roles.find((role) => role.id === target.roleId)?.permissions ?? [];
      return {
        isOwner: false,
        canEditCompany: grid.includes(COMPANY_SETTINGS_PERMISSION),
        canOpen: grid.includes(OPEN_SCREEN_PERMISSION),
      };
    }
    case 'self':
      // Never used to draw — the caller renders its own rows untouched when the
      // preview is off. Returned so the function is total.
      return { isOwner: false, canEditCompany: false, canOpen: false };
  }
}

/**
 * The `<select>`'s value for a target, and back.
 *
 * A `<select>` carries strings, so the union has to survive a round trip
 * through one. `decodeViewAs` answers `self` for anything it does not
 * recognise — a value naming no option is exactly the ambiguity recorded in
 * the protocol's `<select>` trap, and "off" is the reading that cannot
 * mislead: the banner disappears with it, so a decode that quietly failed
 * cannot leave somebody previewing a role they can no longer see named.
 */
export function encodeViewAs(target: ViewAsTarget): string {
  return target.kind === 'role' ? `role:${target.roleId}` : target.kind;
}

export function decodeViewAs(value: string): ViewAsTarget {
  if (value === 'owner' || value === 'admin' || value === 'noRole') return { kind: value };
  if (value.startsWith('role:')) return { kind: 'role', roleId: value.slice('role:'.length) };
  return VIEW_AS_OFF;
}

/**
 * The rows `GET /api/settings` would have answered with for that identity.
 *
 * Mirrors `SettingsService.list()`:
 *
 *   BR-ADM-01-02  an owner-only row is INVISIBLE to everybody but the Owner —
 *                 dropped, never dimmed, "because that would leak that it
 *                 exists". So this filters rather than flags.
 *   BR-ADM-01-03  a personal setting is always changeable by its owner.
 *   BR-ADM-01-04  an organization or role setting is changeable only by the
 *                 Owner or a holder of the Administrator role.
 *
 * NOTE WHAT IS NOT RECOMPUTED, and that it is a fidelity decision rather than
 * an omission: `value`, `effectiveValue`, `overridden` and `updatedAt` stay as
 * they arrived. They are the CALLER'S data — the previewed person's own
 * overrides live in their `user_settings` rows and are not readable from here,
 * and the only way to obtain them would be to ask the server for somebody
 * else's settings, which is the impersonation this control is specified not to
 * be. The preview answers "what would they SEE and what could they CHANGE",
 * which is the question §6 asks it; it does not claim to answer "what value
 * would they read", and the banner says preview for exactly this reason.
 */
export function previewRows(
  rows: readonly SettingRow[],
  authority: PreviewAuthority,
): SettingRow[] {
  const out: SettingRow[] = [];
  for (const row of rows) {
    if (row.scope === 'owner' && !authority.isOwner) continue;
    out.push({
      ...row,
      canChange:
        row.scope === 'owner'
          ? authority.isOwner
          : row.scope === 'personal'
            ? true
            : authority.canEditCompany,
    });
  }
  return out;
}
